import React, { useState, useEffect } from 'react'
import { VolunteerFormModalMetaProps, Volunteer, Preference, Flag, Duty } from '../data/types'
import { Button, CheckboxGroup } from './ui'
import { Modal } from './ui/Modal'
import { API_BASE_URL, AllLanguages, VOLUNTEER_FOR_VISIT_MAP_INVERSE, AVAILABILITY_OPTIONS, IVPositions } from '../data'
import { useVisitAnnounced } from '../hooks/useGlobalSetting'
import { capitalizeFirstLetter } from '../utils/volunteers'
import { XIcon } from '../icons'



type Props = {
    meta: VolunteerFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (VolunteerData: any) => Promise<void>
    initialVolunteer?: Volunteer | null
    adminSide?: boolean
}

const normalizeServiceRecord = (serviceRecord?: string | null) => {
    const trimmedServiceRecord = serviceRecord?.trim()

    if (!trimmedServiceRecord) {
        return ''
    }

    return IVPositions.find(position => position.toLowerCase() === trimmedServiceRecord.toLowerCase()) || trimmedServiceRecord
}


export const VolunteerFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialVolunteer, adminSide } = props


    const [formData, setFormData] = useState({
        csid: 0,
        first_name: '',
        last_name: '',
        second_last_name: '',
        gender: '',
        dob: '',
        phone: '',
        country_code: '',
        email: '',
        country: '',
        country_of_birth: '',
        jamat_khana: '',
        family_origin: '',
        service_location: '',
        service_record: '',
        languages: '',
        support_needs: '',
        ismaili: false,
        multi_faith_family: false,
        registered_volunteer: false,
        eligible_for_duty: true,
        volunteer_for_visit: '',
        uk_didar_registration_ack: false,
        international_volunteer_registration_ack: false,
        dbs_expiry: '',
        first_aid_expiry: ''
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [showSuccess, setShowSuccess] = useState(false)
    const [availablePreferences, setAvailablePreferences] = useState<Preference[]>([])
    const [selectedPreferences, setSelectedPreferences] = useState<number[]>([])
    const [availableFlags, setAvailableFlags] = useState<Flag[]>([])
    const [selectedFlags, setSelectedFlags] = useState<number[]>([])
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
    const [selectedAvailability, setSelectedAvailability] = useState<string[]>([])
    const [negativeListDuties, setNegativeListDuties] = useState<Duty[]>([]);
    const [selectedNegativeDuty, setSelectedNegativeDuty] = useState<string[]>([]);
    const { value: visitAnnounced, loading: loadingVisitAnnounced } = useVisitAnnounced()

    // Group flags by category
    const flagsByCategory: { [key: string]: Flag[] } = {}
    const uncategorizedFlags: Flag[] = []

    availableFlags.forEach((flag) => {
        if (flag.category) {
            const categoryName = flag.category.name
            if (!flagsByCategory[categoryName]) {
                flagsByCategory[categoryName] = []
            }
            flagsByCategory[categoryName].push(flag)
        } else {
            uncategorizedFlags.push(flag)
        }
    })

    // Sort category names and flags within categories alphabetically
    const sortedCategoryNames = Object.keys(flagsByCategory).sort()
    Object.keys(flagsByCategory).forEach(categoryName => {
        flagsByCategory[categoryName].sort((a, b) => a.name.localeCompare(b.name))
    })
    uncategorizedFlags.sort((a, b) => a.name.localeCompare(b.name))


    // Fetch available preferences
    useEffect(() => {

        const fetchPreferences = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/preference`, {
                    credentials: 'include'
                })
                if (response.ok) {
                    const result = await response.json()
                    let data = result.value || result
                    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                        data = data[0]
                    }
                    setAvailablePreferences(Array.isArray(data) ? data : [])
                }
            } catch (err) {
                console.error('Failed to fetch preferences:', err)
            }
        }

        const fetchFlags = async () => {
            try {
                const flagUrl = adminSide ? `${API_BASE_URL}/flag` : `${API_BASE_URL}/flag?registration=1`
                const response = await fetch(flagUrl, {
                    credentials: 'include'
                })
                if (response.ok) {
                    const result = await response.json()
                    let data = result.value || result
                    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                        data = data[0]
                    }
                    setAvailableFlags(Array.isArray(data) ? data : [])
                }
            } catch (err) {
                console.error('Failed to fetch flags:', err)
            }
        }

        const fetchNegativeListDuties = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/duty`, {
                    credentials: 'include'
                });

                if (response.ok) {
                    const result = await response.json();
                    let data = result.value || result;
                    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                        data = data[0];
                    }
                    setNegativeListDuties(Array.isArray(data) ? data.filter(d => d.negative_list_member) : []);
                }

            }
            catch (error) {
                console.log('Failed to fetch negative list duties: ', error);
            }
        }

        const fetchData = async () => {
            await Promise.all([
                fetchPreferences(),
                fetchFlags(),
                //fetchNegativeListDuties()
            ]);
        }

        if (isOpen) {
            fetchData();
        }

    }, [isOpen])

    // Populate form when editing a volunteer
    useEffect(() => {
        if (initialVolunteer && isOpen) {
            const defaultServiceRecord = normalizeServiceRecord(initialVolunteer.service_record);

            setFormData({
                csid: initialVolunteer.csid || 0,
                first_name: initialVolunteer.first_name || '',
                last_name: initialVolunteer.last_name || '',
                second_last_name: initialVolunteer.second_last_name || '',
                gender: initialVolunteer.gender || '',
                dob: initialVolunteer.dob || '',
                phone: initialVolunteer.phone || '',
                country_code: initialVolunteer.country_code || '',
                email: initialVolunteer.email || '',
                country: initialVolunteer.country || '',
                country_of_birth: initialVolunteer.country_of_birth || '',
                jamat_khana: initialVolunteer.jamat_khana || '',
                family_origin: initialVolunteer.family_origin || '',
                service_location: initialVolunteer.service_location || '',
                service_record: defaultServiceRecord,
                languages: initialVolunteer.languages || '',
                support_needs: initialVolunteer.support_needs || '',
                ismaili: initialVolunteer.ismaili || false,
                multi_faith_family: initialVolunteer.multi_faith_family || false,
                registered_volunteer: initialVolunteer.registered_volunteer || false,
                eligible_for_duty: initialVolunteer.eligible_for_duty || false,
                volunteer_for_visit: initialVolunteer.volunteer_for_visit !== null &&
                    initialVolunteer.volunteer_for_visit !== undefined ?
                    String(initialVolunteer.volunteer_for_visit) : '',
                uk_didar_registration_ack: initialVolunteer.uk_didar_registration_ack || false,
                international_volunteer_registration_ack: initialVolunteer.international_volunteer_registration_ack || false,
                dbs_expiry: initialVolunteer.dbs_expiry || '',
                first_aid_expiry: initialVolunteer.first_aid_expiry || ''
            })
            // Set selected preferences
            if (initialVolunteer.preferences) {
                setSelectedPreferences(initialVolunteer.preferences.map(p => p.id))
            }
            // Set selected flags
            if (initialVolunteer.flags) {
                setSelectedFlags(initialVolunteer.flags.map(f => f.id))
            }

            // Set selected availability
            if (initialVolunteer.availability && Array.isArray(initialVolunteer.availability)) {
                setSelectedAvailability(initialVolunteer.availability)
            } else {
                setSelectedAvailability([])
            }

            // Set selected negative list members
            // if (initialVolunteer.negative_list_members && Array.isArray(initialVolunteer.negative_list_members)) {
            //     setSelectedNegativeDuty(initialVolunteer.negative_list_members);
            // }
            // else {
            //     setNegativeListDuties([]);
            // }


            // Parse languages from comma-separated string to array
            if (initialVolunteer.languages) {
                const languagesArray = initialVolunteer.languages.split(',').map(lang => lang.trim()).filter(lang => lang.length > 0)
                setSelectedLanguages(languagesArray)
            } else {
                setSelectedLanguages([])
            }
        } else if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                csid: 0,
                first_name: '',
                last_name: '',
                second_last_name: '',
                gender: '',
                dob: '',
                phone: '',
                country_code: '',
                email: '',
                country: '',
                country_of_birth: '',
                jamat_khana: '',
                family_origin: '',
                service_location: '',
                service_record: '',
                languages: '',
                support_needs: '',
                ismaili: false,
                multi_faith_family: false,
                registered_volunteer: false,
                eligible_for_duty: true,
                volunteer_for_visit: '',
                uk_didar_registration_ack: false,
                international_volunteer_registration_ack: false,
                dbs_expiry: '',
                first_aid_expiry: ''
            })
            setSelectedPreferences([])
            setSelectedFlags([])
            setSelectedLanguages([])
            setSelectedAvailability([])
            setError(null)
            setShowSuccess(false)
        }
    }, [initialVolunteer, isOpen])

    // Clear availability when volunteer_for_visit changes to "No"
    useEffect(() => {
        if (formData.volunteer_for_visit !== '1') {
            setSelectedAvailability([])
        }
    }, [formData.volunteer_for_visit])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target
        setFormData(prev => ({ ...prev, [name]: checked }))
    }

    const handlePreferenceToggle = (preferenceId: number) => {
        setSelectedPreferences(prev => {
            if (prev.includes(preferenceId)) {
                return prev.filter(id => id !== preferenceId)
            } else {
                return [...prev, preferenceId]
            }
        })
    }

    const handleFlagToggle = (flagId: number) => {
        setSelectedFlags(prev => {
            if (prev.includes(flagId)) {
                return prev.filter(id => id !== flagId)
            } else {
                return [...prev, flagId]
            }
        })
    }



    const certificationOptions = [
        { value: "Doctor", label: "Doctor" },
        { value: "Nurse", label: "Nurse" },
        { value: "First Aid", label: "First Aid" },
        { value: "Police", label: "Police" },
        { value: "Fire Marshall", label: "Fire Marshall" },
        { value: "SIA Security", label: "SIA Security" },
    ];

    const allLanguages = AllLanguages;

    const allCountries = [
        "United States", "India", "China", "Brazil", "Russia", "Germany",
        "United Kingdom", "France", "Japan", "Canada", "Australia", "Mexico",
        "Pakistan", "Bangladesh", "Nigeria", "Indonesia", "South Korea", "Other"
    ];

    const regexCheck = (value: string, regExp: RegExp) => {
        return regExp.test(value);
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {

            // const countryCodeRegex: RegExp = /^\+[0-9]+$/;
            // const phoneRegex: RegExp = /^[0-9]+$/;

            // Country code validation
            // if (!regexCheck(formData.country_code, countryCodeRegex)) {
            //     throw new Error("Invalid country code. Use format like +92 or +44");
            // }

            // Phone number validation
            // if (!regexCheck(formData.phone, phoneRegex)) {
            //     throw new Error('Invalid phone number.');
            // }

            if (selectedLanguages.length === 0) {
                throw new Error('Please select at least one language.')
            }

            if (visitAnnounced && String(formData.volunteer_for_visit) === '1' && !selectedAvailability.some(a => AVAILABILITY_OPTIONS.includes(a as any))) {
                throw new Error('Please select at least one availability option.')
            }

            const formattedData = {
                ...formData,
                volunteer_for_visit: formData.volunteer_for_visit === '' ? null : Number(formData.volunteer_for_visit),
                availability: selectedAvailability,
                languages: selectedLanguages.join(', '),
                preferences: selectedPreferences,
                flags: selectedFlags,
                negative_list_members: selectedNegativeDuty,
            }
            await onSubmit(formattedData)
            setShowSuccess(true)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    const handleLanguageToggle = (language: string) => {
        setSelectedLanguages(prev => {
            if (prev.includes(language)) {
                return prev.filter(lang => lang !== language)
            } else {
                return [...prev, language]
            }
        })
    }

    const handleAvailabilityToggle = (availability: string) => {
        setSelectedAvailability(prev => {
            if (prev.includes(availability)) {
                return prev.filter(avail => avail !== availability)
            } else {
                return [...prev, availability]
            }
        })
    }

    const handleNegativeDutyToggle = (duty: string) => {
        setSelectedNegativeDuty(prev => {
            if (prev.includes(duty)) {
                return prev.filter(d => d !== duty)
            }
            else {
                return [...prev, duty];
            }
        })
    }

    let fieldsToLock = ['first_name', 'last_name', 'age', 'mobile', 'email', 'gender', 'phone', 'country_code', 'country', 'jamat_khana', 'family_origin', 'ismaili', 'registered_volunteer', 'dob'];
    fieldsToLock = fieldsToLock.map(f => f.toLowerCase())
    const fieldsToLockSet = new Set(fieldsToLock)

    const isLocked = (fieldName: string): boolean => {

        // Fields should be locked if:
        // 1. The field is in the fieldsToLock list
        // 2. There is an initial volunteer (editing, not creating)
        // 3. EITHER:
        //    a. It's an IIUK volunteer (iiuk_registeration is truthy or null/undefined)
        //    b. OR it's a non-IIUK volunteer but we're NOT on the admin side
        const hasInitialVolunteer = initialVolunteer !== null && initialVolunteer !== undefined;

        // Check if non-IIUK: iiuk_registeration is explicitly false or 0
        const isNonIIUKVolunteer = initialVolunteer?.iiuk_registeration === false ||
            initialVolunteer?.iiuk_registeration === 0;

        // IIUK volunteer is anyone who is NOT non-IIUK (including null/undefined/true/1)
        const isIIUKVolunteer = hasInitialVolunteer && !isNonIIUKVolunteer;

        // If it's not a field we want to lock at all, return false
        if (!fieldsToLockSet.has(fieldName.toLowerCase())) {
            return false;
        }

        // If there's no initial volunteer (creating new), don't lock
        if (!hasInitialVolunteer) {
            return false;
        }

        // For IIUK volunteers, always lock
        if (isIIUKVolunteer) {
            return true;
        }

        // For non-IIUK volunteers:
        // - Lock if NOT on admin side (user's own profile)
        // - Unlock if on admin side (admin editing)
        if (isNonIIUKVolunteer) {
            return adminSide !== true;
        }

        // Default: don't lock
        return false;
    }

    const localCountries = ['austria', 'cyprus', 'denmark', 'estonia', 'finland', 'germany', 'hungary', 'italy', 'netherlands', 'north macedonia', 'norway', 'poland', 'sweden', 'slovenia', 'lithuania', 'greece'];
    const isLocalVolunteer = localCountries.some(c => initialVolunteer?.country?.toLowerCase().includes(c));
    const isInternationalVolunteer = initialVolunteer?.international_volunteer === true || !isLocalVolunteer;
    const serviceRecordOptions = formData.service_record && !IVPositions.includes(formData.service_record)
        ? [formData.service_record, ...IVPositions]
        : IVPositions

    const handleExpiryChange = (categoryId: number, value: string) => {
        setFormData(prev => {
            if (categoryId === 2) {
                return { ...prev, dbs_expiry: value }
            } else {
                return { ...prev, first_aid_expiry: value }
            }
        })
    }

    const handleSuccessClose = () => {
        setShowSuccess(false)
        onClose()
    }

    if (showSuccess) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={handleSuccessClose}
                title={meta.title}
                maxWidth="2xl"
                footer={
                    <div className="flex justify-end">
                        <Button
                            type="button"
                            onClick={handleSuccessClose}
                            className="w-full sm:w-auto"
                        >
                            {meta.closeButton}
                        </Button>
                    </div>
                }
            >
                <div className="px-4 sm:px-6 py-8 text-center">
                    <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-success-100 dark:bg-success-900/30 mb-4">
                        <svg className="w-6 h-6 text-success-600 dark:text-success-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        {meta.successMessage}
                    </p>
                </div>
            </Modal>
        )
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="2xl"
            footer={
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        variant="outline"
                        color="neutral"
                        className="w-full sm:w-auto"
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        form="volunteer-form"
                        disabled={loading}
                        className="w-full sm:w-auto"
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="volunteer-form" onSubmit={handleSubmit}>
                <div className="px-4 sm:px-6 py-4 max-h-[70vh] overflow-y-auto">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    {
                        !isLocalVolunteer && !adminSide && (
                            <div className='flex items-start gap-3 mb-4'>
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                                    <span className="text-primary-600 dark:text-primary-400 font-bold text-sm">!</span>
                                </span>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    If you are already planning to travel and would like to volunteer, please register your interest. Volunteering is based on personal arrangements, and all travel, accommodation, visa, and related requirements must be arranged independently.
                                </p>
                            </div>
                        )
                    }

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                        {!(isLocked('first_name') && !adminSide) && (<div>
                            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.nameLabel}
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                name="first_name"
                                required
                                readOnly={isLocked('first_name')}
                                disabled={isLocked('first_name')}
                                value={formData.first_name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                                placeholder={meta.namePlaceholder}

                            />
                        </div>)}

                        {!(isLocked('last_name') && !adminSide) && (<div>
                            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.lastNameLabel}
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                name="last_name"
                                required
                                readOnly={isLocked('last_name')}
                                disabled={isLocked('last_name')}
                                value={capitalizeFirstLetter(formData.last_name)}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                                placeholder={meta.lastNamePlaceholder}
                            />
                        </div>)}

                        {/* {adminSide && <div>
                            <label htmlFor="second_last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.secondLastNameLabel}
                            </label>
                            <input
                                type="text"
                                id="second_last_name"
                                name="second_last_name"
                                value={capitalizeFirstLetter(formData.second_last_name)}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                                placeholder={meta.secondLastNamePlaceholder}
                            />
                        </div>} */}

                        {!(isLocked('gender') && !adminSide) && (<div>
                            <label htmlFor="gender" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.genderLabel}
                            </label>
                            <select
                                id="gender"
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                disabled={isLocked('gender')}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 bg-gray-50 dark:text-gray-100 disabled:opacity-50"
                            >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Prefer Not to Say">Prefer Not to Say</option>
                            </select>
                        </div>)}

                        {/* {adminSide && (<select
                                id="gender"
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                            >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>)} */}

                        {adminSide && (<div>
                            <label htmlFor="dob" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.dobLabel} {/*<span className="text-danger-500">*</span>*/}
                            </label>
                            <input
                                type="date"
                                id="dob"
                                name="dob"
                                readOnly={isLocked('dob')}
                                disabled={isLocked('dob')}
                                required
                                value={formData.dob}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                            />
                        </div>)}

                        {!(isLocked('email') && !adminSide) && (<div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.EmailLabel}
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                required
                                readOnly={isLocked('email')}
                                disabled={isLocked('email')}
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                                placeholder={meta.EmailPlaceholder}
                            />
                        </div>)}

                        {!(isLocked('country_code') && !adminSide) && (<div>
                            <label htmlFor="country_code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.countryCodeLabel}
                            </label>
                            <input
                                type="text"
                                id="country_code"
                                name="country_code"
                                value={formData.country_code}
                                disabled={isLocked('country_code')}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                                placeholder="e.g. +44"
                            />
                        </div>)}

                        {!(isLocked('phone') && !adminSide) && (<div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.contactLabel}
                            </label>
                            <input
                                type="text"
                                id="phone"
                                name="phone"
                                required
                                readOnly={isLocked('phone')}
                                disabled={isLocked('phone')}
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                                placeholder={meta.contactPlaceholder}
                            />
                        </div>)}

                        {!(isLocked('country') && !adminSide) && (<div>
                            <label
                                htmlFor="country"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.CountryofResidence}
                            </label>
                            <input
                                type="text"
                                id="country"
                                name="country"
                                required
                                readOnly={isLocked('country')}
                                disabled={isLocked('country')}
                                value={formData.country}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                                placeholder="Enter Country"
                            />
                        </div>)}


                        {/* {adminSide && (<div>
                            <label
                                htmlFor="country_of_birth"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.countryOfBirthLabel}
                            </label>
                            <select
                                id="country_of_birth"
                                name="country_of_birth"
                                value={formData.country_of_birth}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                            >
                                <option value="" disabled>
                                    Select Country of Birth
                                </option>
                                {allCountries.map((country) => (
                                    <option key={country} value={country}>
                                        {country}
                                    </option>
                                ))}
                            </select>
                        </div>)} */}

                        {!(isLocked('jamat_khana') && !adminSide) && (<div>
                            <label
                                htmlFor="jamat_khana"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.Jamatkhana}
                            </label>
                            <input
                                type="text"
                                id="jamat_khana"
                                name="jamat_khana"

                                readOnly={isLocked('jamat_khana')}
                                disabled={isLocked('jamat_khana')}
                                value={formData.jamat_khana}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                                placeholder="Enter Jamatkhana"
                            />
                        </div>)}

                        {!(isLocked('family_origin') && !adminSide) && (<div>
                            <label
                                htmlFor="family_origin"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                {meta.FamilyOriginLabel}
                            </label>
                            <select
                                id="family_origin"
                                name="family_origin"
                                value={formData.family_origin}
                                onChange={handleChange}
                                disabled={isLocked('family_origin')}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 bg-gray-50 dark:text-gray-100 disabled:opacity-50"
                            >
                                <option value="">Select Origin</option>
                                <option value="East Africa">East Africa</option>
                                <option value="Indian Subcontinent">Indian Subcontinent (India / Pakistan / Bangladesh)</option>
                                <option value="Afghanistan">Afghanistan</option>
                                <option value="Tajikistan">Tajikistan</option>
                                <option value="Syria">Syria</option>
                                <option value="Iran">Iran</option>
                                <option value="Mixed Origin">Mixed Origin</option>
                                <option value="Other">Other</option>
                            </select>

                        </div>)}

                        {/* {adminSide && (<div>
                            <label
                                htmlFor="service_location"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.serviceLocationLabel}
                            </label>
                            <input
                                type="text"
                                id="service_location"
                                name="service_location"
                                value={formData.service_location}
                                readOnly={isLocked('service_location')}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="Enter Service Location"
                            />
                        </div>)} */}

                        <div>
                            <label
                                htmlFor="service_record"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.serviceRecordLabel} <span className="text-danger-600">*</span>
                            </label>
                            <select
                                id="service_record"
                                name="service_record"
                                value={formData.service_record}
                                onChange={handleChange}
                                disabled={isLocked('service_record')}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                            >
                                <option value="">Select Position</option>
                                {serviceRecordOptions.map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                            {/* <input
                                type="text"
                                id="service_record"
                                name="service_record"
                                value={formData.service_record}
                                readOnly={isLocked('service_record')}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="Enter Service Record"
                            /> */}
                        </div>

                        {/* Europe Didar (Berlin) Registration Note with Acknowledgment - only for IIUK registered volunteers */}
                        {visitAnnounced && isLocalVolunteer && !adminSide && (
                            <div className="md:col-span-2">
                                <div className="p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-md">
                                    <p className="text-sm text-primary-800 dark:text-primary-200 mb-3">
                                        {meta.registrationNote()}
                                    </p>
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="uk_didar_registration_ack"
                                            checked={formData.uk_didar_registration_ack}
                                            onChange={handleCheckboxChange}
                                            required
                                            className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                                        />
                                        <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
                                            {meta.registrationAckLabel} <span className='text-danger-600'>*</span>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* International Volunteer Registration Acknowledgment - only for international volunteers */}
                        {isInternationalVolunteer && !adminSide && (
                            <div className="md:col-span-2">
                                <div className="p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-md">
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="international_volunteer_registration_ack"
                                            checked={formData.international_volunteer_registration_ack}
                                            onChange={handleCheckboxChange}
                                            required
                                            className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                                        />
                                        <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
                                            By registering as an international volunteer, I confirm that my visit to Germany is solely to support volunteer activities related to the Europe visit. <span className='text-danger-600'>*</span>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {visitAnnounced && !(isLocked('volunteer_for_visit') && !adminSide) && (<div>
                            <label
                                htmlFor="volunteer_for_visit"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                {meta.volunteerForVisit} <span className='text-danger-600'>*</span>
                            </label>
                            <select
                                id="volunteer_for_visit"
                                name="volunteer_for_visit"
                                value={formData.volunteer_for_visit}
                                required
                                onChange={handleChange}
                                disabled={isLocked('volunteer_for_visit')}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 bg-gray-50 dark:text-gray-100 disabled:opacity-50"
                            >
                                <option value="" disabled>
                                    No Selection
                                </option>


                                {Object.entries(VOLUNTEER_FOR_VISIT_MAP_INVERSE).map(([label, value]) => (
                                    <option key={value} value={String(value)}>{label}</option>
                                ))}
                            </select>

                        </div>)}
                        {visitAnnounced && formData.volunteer_for_visit === '1' && (
                            <CheckboxGroup
                                label={meta.availabilityLabel}
                                required
                                options={AVAILABILITY_OPTIONS.map(opt => ({ id: opt, label: opt }))}
                                selectedValues={selectedAvailability}
                                onChange={handleAvailabilityToggle}
                                columns={3}
                                bordered={false}
                                className="md:col-span-2"
                            />
                        )}

                    </div>
                    <div className="grid grid-cols-1 gap-4 mt-4">
                        {adminSide && (<div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {meta.volunteerStatusLabel}
                            </label>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="flex items-center gap-2 py-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="ismaili"
                                        checked={formData.ismaili}
                                        readOnly={isLocked('ismaili')}
                                        onChange={handleCheckboxChange}
                                        className="h-5 w-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">{meta.ismailiLabel}</span>
                                </label>

                                {/* <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        name="multi_faith_family"
                                        readOnly={isLocked('multi_faith_family')}
                                        checked={formData.multi_faith_family}
                                        onChange={handleCheckboxChange}
                                        className="h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">{meta.multiFaithFamilyLabel}</span>
                                </label> */}

                                <label className="flex items-center gap-2 py-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="registered_volunteer"
                                        readOnly={isLocked('registered_volunteer')}
                                        checked={formData.registered_volunteer}
                                        onChange={handleCheckboxChange}
                                        className="h-5 w-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">{meta.registeredVolunteerLabel}</span>
                                </label>

                                <label className="flex items-center gap-2 py-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="eligible_for_duty"
                                        readOnly={isLocked('eligible_for_duty')}
                                        checked={formData?.eligible_for_duty}
                                        onChange={handleCheckboxChange}
                                        className="h-5 w-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">{meta.eligibleForDutyLabel}</span>
                                </label>
                            </div>
                        </div>)}

                        {/* Flags Section - By Category */}
                        {/* TODO: Handle edge case if expiery_flags are unselected after submitting expiry values*/}
                        {sortedCategoryNames.map((categoryName) => {

                            const currentCategoryFlags = flagsByCategory[categoryName];
                            // Use optional chaining just in case
                            const categoryId = currentCategoryFlags.length > 0 ? currentCategoryFlags[0].category_id : null;

                            const showExpiry = currentCategoryFlags.some(flag =>
                                selectedFlags.includes(flag.id) && flag.has_expiry
                            );

                            // Determine the current value based on category logic
                            const currentValue = categoryId === 2 ? formData.dbs_expiry : formData.first_aid_expiry;

                            return (
                                <>
                                    <CheckboxGroup
                                        label={categoryName}
                                        options={flagsByCategory[categoryName].map(flag => ({ id: flag.id, label: flag.name }))}
                                        selectedValues={selectedFlags}
                                        onChange={handleFlagToggle}
                                        columns={3}
                                        className="md:col-span-2"
                                    />

                                    {showExpiry && categoryId && (
                                        <div className="md:col-span-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md animate-fadeIn">
                                            <label
                                                htmlFor={`expiry-${categoryId}`}
                                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                            >
                                                Expiry Date for {categoryId === 1 ? "First Aid" : "DBS"} Certificate
                                            </label>

                                            <div className="flex items-center gap-2 sm:w-1/2">
                                                <input
                                                    type="date"
                                                    id={`expiry-${categoryId}`}
                                                    value={currentValue || ''}
                                                    onChange={(e) => handleExpiryChange(categoryId, e.target.value)}
                                                    // Only require it if we don't have a value yet (optional UI preference)
                                                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                                />

                                                {/* Clear Button - Only shows if there is a value */}
                                                {currentValue && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleExpiryChange(categoryId, '')}
                                                        className="p-2 text-gray-400 hover:text-danger-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                                        title="Clear Date"
                                                    >
                                                        <XIcon className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )
                        })}
                        {/* Flags Section - Uncategorized */}
                        {uncategorizedFlags.length > 0 && (
                            <CheckboxGroup
                                label={meta.flagsLabel}
                                options={uncategorizedFlags.map(flag => ({ id: flag.id, label: flag.name }))}
                                selectedValues={selectedFlags}
                                onChange={handleFlagToggle}
                                columns={3}
                                loading={availableFlags.length === 0}
                                loadingMessage="Loading flags..."
                                className="md:col-span-2"
                            />
                        )}

                        {/* Preferences Section */}
                        {adminSide && (
                            <CheckboxGroup
                                label={meta.preferencesLabel}
                                options={availablePreferences.map(pref => ({ id: pref.id, label: pref.name }))}
                                selectedValues={selectedPreferences}
                                onChange={handlePreferenceToggle}
                                columns={3}
                                loading={availablePreferences.length === 0}
                                loadingMessage="Loading preferences..."
                                className="md:col-span-2"
                            />
                        )}

                        {/* Language Section */}
                        <CheckboxGroup
                            label={meta.languagesLabel}
                            required
                            options={allLanguages.map(lang => ({ id: lang, label: lang }))}
                            selectedValues={selectedLanguages}
                            onChange={handleLanguageToggle}
                            columns={3}
                            className="md:col-span-2"
                        />
                        {selectedLanguages.length > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 md:col-span-2">
                                Selected: {selectedLanguages.join(', ')}
                            </p>
                        )}

                        {/* Negative List Members */}
                        {/* {negativeListDuties.length > 0 &&
                            <div>
                                <CheckboxGroup
                                    label={meta.negativeListLabel}
                                    options={negativeListDuties.map(duty => ({ id: duty.name, label: duty.name }))}
                                    selectedValues={selectedNegativeDuty}
                                    onChange={handleNegativeDutyToggle}
                                    columns={2}
                                    className='md:col-span-2'
                                />
                            </div>
                        } */}

                        <div className="md:col-span-2">
                            <label
                                htmlFor="support_needs"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.supportNeedsLabel}
                            </label>
                            <textarea
                                id="support_needs"
                                name="support_needs"
                                value={formData.support_needs}
                                onChange={handleChange}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.supportNeedsPlaceholder}
                            />
                        </div>

                    </div>
                </div>
            </form>
        </Modal >
    )
}
